import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { UserRole } from '@manpower/shared';
import { useAuth } from '@/auth/AuthProvider';
import { api } from '@/lib/api';
import { COMMANDS, resultHref, type SearchGroup } from '@/lib/search';
import { Icon } from '@/components/primitives';

/** Long enough that a single letter does not fan out across five tables. */
const MIN_QUERY = 2;

/**
 * Typing pause before the request. Short enough to feel live, long enough that
 * "sundaram" is one query rather than eight.
 */
const DEBOUNCE_MS = 250;

/**
 * One box over everything, plus the commands a heavy user would otherwise hunt
 * through the sidebar for.
 *
 * The palette is opened with the search field or Ctrl/⌘-K and closed with
 * Escape. It is deliberately the same component for both: a separate command
 * palette would mean two overlays racing for the same shortcut, and a recruiter
 * looking for "Sundaram" and a recruiter wanting "create vacancy" both start by
 * typing.
 *
 * Commands are filtered client-side from a static list and never come from the
 * server: they are navigation, not data, and each is already permission-gated
 * by the route it leads to.
 */
export function GlobalSearch() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState('');
  const [debounced, setDebounced] = useState('');
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const id = setTimeout(() => setDebounced(term.trim()), DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [term]);

  // Ctrl/⌘-K from anywhere. Bound on the window rather than the field so it
  // works while the caret is in a form three screens deep.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setOpen(true);
      }
      if (event.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (open) inputRef.current?.focus();
    else {
      setTerm('');
      setDebounced('');
      setActive(0);
    }
  }, [open]);

  const results = useQuery({
    queryKey: ['search', debounced],
    queryFn: ({ signal }) => api.get<SearchGroup[]>('/search', { q: debounced }, signal),
    enabled: open && debounced.length >= MIN_QUERY,
    // Results go stale the moment anything is edited elsewhere, but within a
    // single palette session repeating a term should not re-query.
    staleTime: 30_000,
  });

  const role = user?.role ?? UserRole.SEEKER;

  const commands = useMemo(() => {
    const needle = debounced.toLowerCase();
    if (!needle) return [];
    return COMMANDS.filter(
      (command) => command.roles.includes(role) && command.label.toLowerCase().includes(needle),
    ).slice(0, 4);
  }, [debounced, role]);

  /**
   * Commands and results flattened into the single list the arrow keys walk.
   * Keeping one array means the highlighted row and the row Enter opens cannot
   * disagree — which they did when each section tracked its own index.
   */
  const rows = useMemo(() => {
    const commandRows = commands.map((command) => ({
      key: `cmd:${command.href}`,
      label: command.label,
      detail: 'Command',
      href: command.href,
      icon: command.icon,
    }));
    const hitRows = (results.data ?? []).flatMap((group) =>
      group.hits.map((hit) => ({
        key: `${group.type}:${hit.id}`,
        label: hit.title,
        detail: [group.label, hit.subtitle].filter(Boolean).join(' · '),
        href: resultHref(group.type, hit.id, role),
        icon: undefined,
      })),
    );
    return [...commandRows, ...hitRows];
  }, [commands, results.data, role]);

  useEffect(() => setActive(0), [rows.length]);

  const go = (href: string) => {
    setOpen(false);
    navigate(href);
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActive((i) => Math.min(i + 1, Math.max(0, rows.length - 1)));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive((i) => Math.max(0, i - 1));
    } else if (event.key === 'Enter') {
      const row = rows[active];
      if (row) {
        event.preventDefault();
        go(row.href);
      }
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm text-ink-500 hover:bg-ink-100 sm:w-56 sm:ring-1 sm:ring-inset sm:ring-ink-500"
        aria-label="Search"
        aria-keyshortcuts="Control+K Meta+K"
      >
        <Icon name="search" size="sm" />
        <span className="hidden sm:block">Search</span>
        {/* The shortcut is advertised rather than hidden: it is the only way a
            heavy user learns the palette exists. */}
        <kbd className="ml-auto hidden rounded border border-ink-300 px-1 text-[10px] text-ink-500 sm:block">
          Ctrl K
        </kbd>
      </button>

      {open && (
        <div
          className="fixed inset-0 z-40 flex items-start justify-center bg-ink-900/40 p-4"
          role="dialog"
          aria-modal="true"
          aria-label="Search"
          onClick={(event) => {
            if (event.target === event.currentTarget) setOpen(false);
          }}
        >
          <div className="mt-16 w-full max-w-xl overflow-hidden rounded-lg bg-white shadow-xl">
            <div className="flex items-center gap-2 border-b border-ink-200 px-4">
              <Icon name="search" size="sm" className="text-ink-400" />
              <input
                ref={inputRef}
                value={term}
                onChange={(event) => setTerm(event.target.value)}
                onKeyDown={onKeyDown}
                placeholder="Search companies, jobs, candidates…"
                className="h-12 flex-1 border-0 text-sm text-ink-900 outline-none placeholder:text-ink-400"
                aria-label="Search"
                aria-autocomplete="list"
                autoComplete="off"
              />
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="rounded p-1 text-ink-400 hover:bg-ink-100"
                aria-label="Close search"
              >
                <Icon name="close" size="sm" />
              </button>
            </div>

            <div className="max-h-80 overflow-y-auto">
              {debounced.length < MIN_QUERY ? (
                <p className="px-4 py-6 text-center text-sm text-ink-500">
                  Type at least {MIN_QUERY} letters.
                </p>
              ) : results.isPending && rows.length === 0 ? (
                <p className="px-4 py-6 text-center text-sm text-ink-500">Searching…</p>
              ) : rows.length === 0 ? (
                <p className="px-4 py-6 text-center text-sm text-ink-500">
                  Nothing matched “{debounced}”.
                </p>
              ) : (
                <ul role="listbox" aria-label="Search results">
                  {rows.map((row, index) => (
                    <li key={row.key}>
                      <button
                        type="button"
                        role="option"
                        aria-selected={index === active}
                        onMouseEnter={() => setActive(index)}
                        onClick={() => go(row.href)}
                        className={`flex w-full items-center gap-3 px-4 py-2.5 text-left ${
                          index === active ? 'bg-brand-50' : 'hover:bg-ink-50'
                        }`}
                      >
                        {row.icon && <Icon name={row.icon} size="sm" className="text-ink-400" />}
                        <span className="min-w-0">
                          <span className="block truncate text-sm text-ink-900">{row.label}</span>
                          {row.detail && (
                            <span className="block truncate text-xs text-ink-500">{row.detail}</span>
                          )}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
